import React from 'react';
import { FontText } from './FontText';
import { useTheme } from '../../contexts/ThemeContext';

export interface CoinOption {
  id: string;
  coins: number;
  price: string;
  bonus?: number;
  recommended?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  price: string;
  displayPrice: string;
  period: string;
  type: 'weekly' | 'yearly';
  discount?: string;
}

export const coinOptions: CoinOption[] = [
  { id: 'coins200', coins: 200, price: '2.99' },
  { id: 'coins600', coins: 600, price: '7.99', bonus: 120 },
  {
    id: 'coins1000',
    coins: 1000,
    price: '12.99',
    bonus: 200,
    recommended: true,
  },
  { id: 'coins5000', coins: 5000, price: '64.99', bonus: 2000 },
];

export interface PaywallFeature {
  key: string;
  textKey: string;
}

export const subscriptionFeatures: PaywallFeature[] = [
  { key: 'unlimited_access', textKey: 'unlimited_access_books' },
  { key: 'new_chapters', textKey: 'new_chapters_monthly' },
  { key: 'mobile_app', textKey: 'comfortable_mobile_app' },
  { key: 'cancel_anytime', textKey: 'cancel_anytime' },
];

interface FeatureListProps {
  features: PaywallFeature[];
  t: (key: string) => string;
  theme: any;
  featureTextStyle: React.CSSProperties;
}

export const FeatureList: React.FC<FeatureListProps> = ({
  features,
  t,
  theme,
  featureTextStyle,
}) => (
  <div
    style={{
      marginTop: 10,
      marginBottom: 20,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
    }}
  >
    {features.map(feature => (
      <div
        key={feature.key}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <img
          src="/img/books/check_icon.png"
          width="21"
          height="21"
          alt="check"
        />
        <FontText style={featureTextStyle}>{t(feature.textKey)}</FontText>
      </div>
    ))}
  </div>
);

interface GuaranteeBadgeProps {
  t: (key: string) => string;
  theme: any;
  guaranteeIconStyle: React.CSSProperties;
  guaranteeTextStyle: React.CSSProperties;
}

export const GuaranteeBadge: React.FC<GuaranteeBadgeProps> = ({
  t,
  guaranteeIconStyle,
  guaranteeTextStyle,
}) => {
  const { themeMode } = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        width: '100%',
        marginBottom: 3,
      }}
    >
      <img
        style={guaranteeIconStyle}
        src={
          themeMode === 'dark'
            ? '/img/books/guarantee_dark.png'
            : '/img/books/guarantee.png'
        }
        alt="guarantee"
      />
      <FontText small style={guaranteeTextStyle}>
        {t('money_back_guarantee')}
      </FontText>
    </div>
  );
};

interface CoinOptionItemProps {
  option: CoinOption;
  isLoading: boolean;
  onClick: (option: CoinOption) => void;
  t: (key: string) => string;
  theme: any;
  themeMode: string;
}

export const CoinOptionItem: React.FC<CoinOptionItemProps> = ({
  option,
  isLoading,
  onClick,
  t,
  theme,
  themeMode,
}) => {
  const styles = {
    coinOption: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      border: `1px solid ${themeMode === 'dark' ? '#374151' : 'rgba(26, 5, 47, 0.2)'}`,
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      backgroundColor: 'transparent',
    } as React.CSSProperties,
    recommendedOption: {
      borderColor:
        themeMode === 'dark' ? theme.colors.accent : theme.colors.black,
      borderWidth: 1,
      marginBottom: 20,
    } as React.CSSProperties,
    disabledOption: {
      opacity: 0.6,
      cursor: 'not-allowed',
    } as React.CSSProperties,
    coinIcon: {
      width: 28,
      height: 28,
    } as React.CSSProperties,
    coinDetails: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    } as React.CSSProperties,
    coinAmount: {
      color: theme.colors.text,
      letterSpacing: '0.13px',
      lineHeight: '16px',
    } as React.CSSProperties,
    coinBonus: {
      color: '#cc20cf',
      letterSpacing: '0.11px',
      lineHeight: '16px',
    } as React.CSSProperties,
    coinPrice: {
      color: theme.colors.text,
      letterSpacing: '0.13px',
      lineHeight: '16px',
      whiteSpace: 'nowrap',
    } as React.CSSProperties,
    recommendedBadge: {
      position: 'absolute',
      bottom: -20,
      left: -1,
      right: -1,
      backgroundColor:
        themeMode === 'dark' ? theme.colors.accent : theme.colors.black,
      textAlign: 'center',
      padding: '6px 4px',
      borderRadius: '0 0 8px 8px',
    } as React.CSSProperties,
  };

  return (
    <div
      style={{
        ...styles.coinOption,
        ...(option.recommended ? styles.recommendedOption : {}),
        ...(isLoading ? styles.disabledOption : {}),
      }}
      onClick={() => !isLoading && onClick(option)}
    >
      <img
        style={styles.coinIcon}
        src={`/img/books/coins_${option.coins}.png`}
        alt={`${option.coins} coins`}
      />
      <div style={styles.coinDetails}>
        <FontText style={styles.coinAmount}>
          {option.coins} {t('coins')}
        </FontText>
        {option.bonus && (
          <FontText style={styles.coinBonus}>
            +{option.bonus} {t('bonus')}
          </FontText>
        )}
      </div>
      <FontText style={styles.coinPrice}>$ {option.price}</FontText>
      {option.recommended && (
        <div style={styles.recommendedBadge}>
          <FontText tiny style={{ color: theme.colors.white }}>
            {t('recommended')}
          </FontText>
        </div>
      )}
    </div>
  );
};

export const createPaywallStyles = (
  theme: any,
  themeMode: string,
  reverseThemeMode?: string
) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.background,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 40px',
    maxWidth: '400px',
    width: '100%',
  } as React.CSSProperties,
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 10,
  } as React.CSSProperties,
  appIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
  } as React.CSSProperties,
  appName: {
    color: theme.colors.text,
  } as React.CSSProperties,
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    maxWidth: '400px',
    alignSelf: 'center',
  } as React.CSSProperties,
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 24,
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 5,
  } as React.CSSProperties,
  tabs: {
    display: 'flex',
    backgroundColor: themeMode === 'dark' ? '#2a2a2a' : '#e3d3d6',
    borderRadius: 8,
    padding: 2,
    marginBottom: 5,
    width: '100%',
  } as React.CSSProperties,
  tab: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 8,
    color: themeMode === 'dark' ? '#9ca3af' : '#87778d',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: 600,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  activeTab: {
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
  } as React.CSSProperties,
  errorContainer: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    width: '100%',
  } as React.CSSProperties,
  errorText: {
    color: '#991b1b',
    margin: 0,
    textAlign: 'center',
  } as React.CSSProperties,
  subscriptionImage: {
    marginTop: -15,
    marginBottom: -15,
    width: 237.7,
    height: 223,
    objectFit: 'cover',
    display: 'block',
  } as React.CSSProperties,
  title: {
    textAlign: 'center',
    marginBottom: 8,
    color: theme.colors.text,
  } as React.CSSProperties,
  subtitle: {
    textAlign: 'center',
    marginBottom: 5,
    color: theme.colors.text,
    marginTop: 2,
  } as React.CSSProperties,
  featureText: {
    color: theme.colors.text,
  } as React.CSSProperties,
  guaranteeIcon: {
    width: 20,
    height: 20,
  } as React.CSSProperties,
  guaranteeText: {
    color: theme.colors.text,
    marginTop: 2,
  } as React.CSSProperties,
  coinOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
    margin: '10px 0',
  } as React.CSSProperties,
  coinsNote: {
    color: theme.colors.textSecondary,
    textAlign: 'center',
    margin: '10px 0',
  } as React.CSSProperties,
  subscriptionPlans: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
    marginBottom: 24,
  } as React.CSSProperties,
  subscriptionPlan: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    position: 'relative',
    flex: 1,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${themeMode === 'dark' ? '#374151' : 'rgba(6, 5, 24, 0.3)'}`,
    backgroundColor: 'transparent',
    color: theme.colors.text,
  } as React.CSSProperties,
  selectedPlan: {
    backgroundColor: themeMode === 'dark' ? theme.colors.accent : '#1e18b4',
    color: themeMode === 'dark' ? theme.colors.text : '#ffffff',
    borderColor: themeMode === 'dark' ? theme.colors.accent : '#1e18b4',
  } as React.CSSProperties,
  planContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
  } as React.CSSProperties,
  planPrice: {} as React.CSSProperties,
  planPeriod: {} as React.CSSProperties,
  planCheck: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 21,
    height: 21,
    padding: 4,
    borderRadius: '50%',
    flexShrink: 0,
    border: `1px solid ${themeMode === 'dark' ? '#374151' : 'rgba(6, 5, 24, 0.3)'}`,
    backgroundColor: 'transparent',
  } as React.CSSProperties,
  discountBadge: {
    position: 'absolute',
    top: '-15%',
    right: '-5%',
    transform: 'translateX(-50%)',
    backgroundColor: '#c1ea2b',
    color: '#1a052f',
    padding: '3px 4px',
    borderRadius: 4,
  } as React.CSSProperties,
  discountText: {} as React.CSSProperties,

  footer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    marginTop: 10,
  } as React.CSSProperties,
  footerLink: {
    color: theme.colors.textSecondary,
    textDecoration: 'none',
  } as React.CSSProperties,
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10002,
  } as React.CSSProperties,
  spinner: {
    width: 40,
    height: 40,
    border: `4px solid ${themeMode === 'dark' ? '#374151' : '#e3d3d6'}`,
    borderTop: '4px solid #1e18b4',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: 16,
  } as React.CSSProperties,
  loaderText: {
    color: theme.colors.text,
    textAlign: 'center',
  } as React.CSSProperties,
});
